import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Phone, 
  Mail, 
  Globe, 
  Send,
  CheckCircle2,
  Loader2
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    product_interest: "",
    message: ""
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, product_interest: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/enquiries`, formData);
      setIsSubmitted(true);
      toast.success("Enquiry submitted successfully! We'll get back to you soon.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        product_interest: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting enquiry:", error);
      toast.error("Failed to submit enquiry. Please try again or contact us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-white border-b border-slate-200" data-testid="contact-hero">
        <div className="section-container py-20 md:py-28">
          <div className="max-w-3xl">
            <div className="accent-line mb-6"></div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 font-['Barlow_Condensed']">
              Contact Us
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl">
              Get in touch with our team for technical specifications, project consultation, 
              or pricing information.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-spacing bg-slate-50" data-testid="contact-form-section">
        <div className="section-container">
          <div className="grid lg:grid-cols-5 gap-12 lg:gap-16">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white border border-slate-200 p-8 lg:p-12">
                <h2 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-2">
                  Send Us an Enquiry
                </h2>
                <p className="text-slate-600 mb-8">
                  Fill out the form below and our team will respond within 24 hours.
                </p>

                {isSubmitted ? (
                  <div className="text-center py-12" data-testid="success-message">
                    <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    <h3 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-2">
                      Thank You!
                    </h3>
                    <p className="text-slate-600 mb-6">
                      Your enquiry has been submitted successfully. We'll get back to you soon.
                    </p>
                    <Button 
                      onClick={() => setIsSubmitted(false)}
                      className="bg-yellow-400 text-slate-900 hover:bg-yellow-500 font-bold uppercase tracking-wider rounded-none"
                      data-testid="submit-another-btn"
                    >
                      Submit Another Enquiry
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="font-medium text-slate-700">
                          Full Name *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="John Smith"
                          className="rounded-none h-12 border-slate-300 focus:border-yellow-400 focus:ring-yellow-400"
                          data-testid="input-name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="font-medium text-slate-700">
                          Email Address *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="john@company.com"
                          className="rounded-none h-12 border-slate-300 focus:border-yellow-400 focus:ring-yellow-400"
                          data-testid="input-email"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="phone" className="font-medium text-slate-700">
                          Phone Number
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+91-9876543210"
                          className="rounded-none h-12 border-slate-300 focus:border-yellow-400 focus:ring-yellow-400"
                          data-testid="input-phone"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="company" className="font-medium text-slate-700">
                          Company Name
                        </Label>
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          value={formData.company}
                          onChange={handleChange}
                          placeholder="ABC Infrastructure Ltd."
                          className="rounded-none h-12 border-slate-300 focus:border-yellow-400 focus:ring-yellow-400"
                          data-testid="input-company"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="product_interest" className="font-medium text-slate-700">
                        Product Interest
                      </Label>
                      <Select onValueChange={handleSelectChange} value={formData.product_interest}>
                        <SelectTrigger 
                          className="rounded-none h-12 border-slate-300 focus:border-yellow-400 focus:ring-yellow-400"
                          data-testid="select-product"
                        >
                          <SelectValue placeholder="Select a product" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="probar">PRO BAR (GFRP Rebar)</SelectItem>
                          <SelectItem value="promesh">PRO MESH (GFRP Mesh)</SelectItem>
                          <SelectItem value="both">Both Products</SelectItem>
                          <SelectItem value="general">General Enquiry</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="font-medium text-slate-700">
                        Your Message *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Please describe your project requirements, quantity needed, or any specific questions..."
                        rows={5}
                        className="rounded-none border-slate-300 focus:border-yellow-400 focus:ring-yellow-400"
                        data-testid="input-message"
                      />
                    </div>

                    <Button 
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-yellow-400 text-slate-900 hover:bg-yellow-500 font-bold uppercase tracking-wider rounded-none h-14 text-base"
                      data-testid="submit-btn"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Submit Enquiry
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>

            {/* Contact Info Sidebar */}
            <div className="lg:col-span-2">
              <div className="bg-slate-900 p-8 lg:p-12 text-white relative noise-overlay h-full">
                <div className="relative z-10">
                  <h2 className="text-2xl font-bold font-['Barlow_Condensed'] mb-8">
                    Get in Touch
                  </h2>

                  <div className="space-y-8">
                    {/* Phone */}
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-yellow-400 flex items-center justify-center">
                          <Phone className="w-5 h-5 text-slate-900" />
                        </div>
                        <span className="font-bold font-['Barlow_Condensed'] text-lg">Phone</span>
                      </div>
                      <a 
                        href="tel:+919871070356" 
                        className="text-slate-300 hover:text-yellow-400 transition-colors text-lg"
                        data-testid="contact-phone"
                      >
                        +91-9871070356
                      </a>
                    </div>

                    {/* Email */}
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-yellow-400 flex items-center justify-center">
                          <Mail className="w-5 h-5 text-slate-900" />
                        </div>
                        <span className="font-bold font-['Barlow_Condensed'] text-lg">Email</span>
                      </div>
                      <a 
                        href="mailto:info@buildedgetech.com" 
                        className="text-slate-300 hover:text-yellow-400 transition-colors"
                        data-testid="contact-email"
                      >
                        info@buildedgetech.com
                      </a>
                    </div>

                    {/* Website */}
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-yellow-400 flex items-center justify-center">
                          <Globe className="w-5 h-5 text-slate-900" />
                        </div>
                        <span className="font-bold font-['Barlow_Condensed'] text-lg">Website</span>
                      </div>
                      <a 
                        href="https://buildedgetech.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-slate-300 hover:text-yellow-400 transition-colors"
                        data-testid="contact-website"
                      >
                        buildedgetech.com
                      </a>
                    </div>
                  </div>

                  <div className="mt-12 pt-8 border-t border-slate-700">
                    <h3 className="font-bold font-['Barlow_Condensed'] mb-4">Office Hours</h3>
                    <p className="text-slate-400 text-sm">
                      Monday - Friday: 9:00 AM - 6:00 PM<br />
                      Saturday: 10:00 AM - 2:00 PM<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Response Promise */}
      <section className="py-16 bg-yellow-400" data-testid="response-promise">
        <div className="section-container text-center">
          <h2 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed'] mb-4">
            We Respond Within 24 Hours
          </h2>
          <p className="text-slate-700 max-w-2xl mx-auto">
            Our engineering team reviews every enquiry personally and provides detailed 
            responses tailored to your specific project requirements.
          </p>
        </div>
      </section>
    </div>
  );
}
